/*
 * assets/product-form.js
 * ------------------------------------------------------------------
 * Progressive enhancement for sections/product.liquid:
 *  - Reads the embedded <script type="application/json"
 *    data-product-json="{{ section.id }}"> blob (= product.variants,
 *    rendered once by Liquid, no extra network request) to resolve
 *    which variant matches the currently-selected option pills.
 *  - Updates price / compare-at price / Add to cart button state /
 *    hidden variant id, and swaps the main gallery image when the
 *    matched variant has its own featured image.
 *  - For a color-type option, also updates the visible color-name
 *    label text next to the swatches when a different swatch is
 *    picked.
 *  - Quantity stepper (+/-), clamped to a minimum of 1 — only wired
 *    up when the markup actually has stepper controls (the v2 PDP
 *    layout submits a fixed hidden quantity of 1 instead).
 *  - "Size & Fit Guide" jump-links ([data-open-accordion]) open the
 *    matching <details> accordion below and scroll it into view.
 *  - A lightweight wishlist heart toggle ([data-wishlist-toggle]),
 *    persisted per product handle in the visitor's own browser via
 *    localStorage — cosmetic/local only, no account-wide "saved
 *    items" feature exists yet.
 *  - Add to cart: real AJAX Cart API POST to routes.cart_add_url +
 *    '.js' (never fakes cart state), then opens/refreshes the SAME
 *    cart drawer used everywhere else on the site via
 *    window.UnnamedCart (exposed by assets/header-interactions.js —
 *    single source of truth, no second/parallel cart UI). If that
 *    hook isn't present (JS error elsewhere, drawer markup missing)
 *    or the request fails, falls back to a real native form submit so
 *    the customer always ends up on a working /cart.
 * ------------------------------------------------------------------
 */
(function () {
  "use strict";

  document.addEventListener("DOMContentLoaded", initAll);
  document.addEventListener("shopify:section:load", initAll);

  function initAll() {
    document.querySelectorAll("[data-product-form]").forEach(initProductForm);
    document.querySelectorAll("[data-product-handle]").forEach(initWishlist);
  }

  function initProductForm(form) {
    if (form.dataset.productFormInit === "true") return;
    form.dataset.productFormInit = "true";

    var wrapper = form.closest('[id^="Product-"]');
    if (!wrapper) return;

    var sectionId = wrapper.id.replace("Product-", "");
    var jsonEl = wrapper.querySelector('[data-product-json="' + sectionId + '"]');
    if (!jsonEl) return;

    var variants;
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }
    if (!Array.isArray(variants) || !variants.length) return;

    var optionEls = form.querySelectorAll("[data-option-index]");
    var variantIdInput = form.querySelector("[data-selected-variant-id]");
    var priceEl = wrapper.querySelector("[data-product-price]");
    var currentPriceEl = wrapper.querySelector("[data-current-price]");
    var compareAtEl = wrapper.querySelector("[data-compare-at-price]");
    var addToCartBtn = form.querySelector("[data-add-to-cart]");
    var addToCartText = form.querySelector("[data-add-to-cart-text]");
    var mainImage = wrapper.querySelector("[data-product-main-image] img");
    var thumbnails = wrapper.querySelectorAll("[data-thumbnail]");
    var qtyInput = form.querySelector("[data-qty-input]");
    var qtyDecrease = form.querySelector("[data-qty-decrease]");
    var qtyIncrease = form.querySelector("[data-qty-increase]");

    var addToCartLabel = addToCartText ? addToCartText.textContent : "";
    var soldOutLabel = priceEl ? priceEl.getAttribute("data-sold-out-text") : "";

    function selectedOptions() {
      var selected = [];
      optionEls.forEach(function (optionEl) {
        var index = parseInt(optionEl.getAttribute("data-option-index"), 10);
        var activeBtn = optionEl.querySelector('[data-selected="true"]');
        selected[index] = activeBtn ? activeBtn.getAttribute("data-option-value") : null;
      });
      return selected;
    }

    function findMatchingVariant(selected) {
      return variants.find(function (variant) {
        return variant.options.every(function (value, index) {
          return selected[index] == null || selected[index] === value;
        });
      });
    }

    function formatMoney(cents) {
      return Math.round(cents / 100) + "€";
    }

    function updateAvailability(selected) {
      optionEls.forEach(function (optionEl) {
        var index = parseInt(optionEl.getAttribute("data-option-index"), 10);
        optionEl.querySelectorAll("[data-option-value]").forEach(function (btn) {
          var testSelection = selected.slice();
          testSelection[index] = btn.getAttribute("data-option-value");
          var anyAvailable = variants.some(function (variant) {
            return (
              variant.available &&
              variant.options.every(function (value, i) {
                return testSelection[i] == null || testSelection[i] === value;
              })
            );
          });
          btn.setAttribute("data-unavailable", anyAvailable ? "false" : "true");
        });
      });
    }

    function applyVariant(variant) {
      if (!variant) {
        if (addToCartBtn) addToCartBtn.disabled = true;
        if (addToCartText) addToCartText.textContent = soldOutLabel || addToCartLabel;
        return;
      }

      if (variantIdInput) variantIdInput.value = variant.id;

      if (currentPriceEl) currentPriceEl.textContent = formatMoney(variant.price);
      if (compareAtEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareAtEl.textContent = formatMoney(variant.compare_at_price);
          compareAtEl.hidden = false;
        } else {
          compareAtEl.hidden = true;
        }
      }
      if (priceEl) {
        if (variant.available) {
          priceEl.removeAttribute("data-sold-out");
        } else {
          priceEl.setAttribute("data-sold-out", "true");
        }
      }

      if (addToCartBtn) addToCartBtn.disabled = !variant.available;
      if (addToCartText) {
        addToCartText.textContent = variant.available ? addToCartLabel : soldOutLabel || addToCartLabel;
      }

      if (variant.featured_image && variant.featured_image.src && mainImage) {
        mainImage.src = variant.featured_image.src;
        mainImage.srcset = "";
      }

      thumbnails.forEach(function (thumb) {
        var matches =
          variant.featured_image &&
          thumb.getAttribute("data-image-src") &&
          thumb.getAttribute("data-image-src").split("?")[0] === variant.featured_image.src.split("?")[0];
        if (matches) thumb.setAttribute("data-active", "true");
      });
    }

    function syncFromSelection() {
      var selected = selectedOptions();
      updateAvailability(selected);
      applyVariant(findMatchingVariant(selected));
    }

    optionEls.forEach(function (optionEl) {
      var colorLabel = optionEl.querySelector("[data-color-label]");

      optionEl.querySelectorAll("[data-option-value]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          optionEl.querySelectorAll("[data-option-value]").forEach(function (sibling) {
            sibling.setAttribute("data-selected", "false");
          });
          btn.setAttribute("data-selected", "true");

          if (colorLabel) {
            colorLabel.textContent = btn.getAttribute("data-option-value");
          }

          syncFromSelection();
        });
      });
    });

    /* "Size & Fit Guide" jump-link: open the matching accordion below
       and scroll it into view. Native <details>/<summary> needs no JS
       to toggle on click, but a link that only points at it still
       needs JS to open + scroll from here. */
    form.querySelectorAll("[data-open-accordion]").forEach(function (link) {
      link.addEventListener("click", function () {
        var targetId = link.getAttribute("data-open-accordion");
        var target = targetId && document.getElementById(targetId);
        if (!target) return;
        target.open = true;
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });

    if (thumbnails.length && mainImage) {
      thumbnails.forEach(function (thumb) {
        thumb.addEventListener("click", function () {
          thumbnails.forEach(function (t) {
            t.setAttribute("data-active", "false");
          });
          thumb.setAttribute("data-active", "true");
          mainImage.src = thumb.getAttribute("data-image-src");
          mainImage.srcset = thumb.getAttribute("data-image-srcset") || "";
          mainImage.alt = thumb.getAttribute("data-image-alt") || mainImage.alt;
        });
      });
    }

    if (qtyInput && qtyDecrease && qtyIncrease) {
      qtyDecrease.addEventListener("click", function () {
        var value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
        qtyInput.value = value;
      });
      qtyIncrease.addEventListener("click", function () {
        var value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + 1);
        qtyInput.value = value;
      });
      qtyInput.addEventListener("change", function () {
        var value = Math.max(1, parseInt(qtyInput.value, 10) || 1);
        qtyInput.value = value;
      });
    }

    form.addEventListener("submit", function (e) {
      /* Real AJAX Cart API call — never fakes cart state. Falls back
         to the form's own native submit (already pointed at
         routes.cart_add_url) on any failure, so the customer always
         ends up somewhere that works. */
      e.preventDefault();

      if (addToCartBtn) addToCartBtn.disabled = true;

      var formData = new FormData(form);
      var payload = {
        id: formData.get("id"),
        quantity: parseInt(formData.get("quantity"), 10) || 1
      };
      if (window.UnnamedCart && window.UnnamedCart.sectionId) {
        payload.sections = window.UnnamedCart.sectionId;
      }

      fetch(form.getAttribute("data-add-to-cart-url") + ".js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          if (!res.ok) throw new Error("Add to cart failed");
          return res.json();
        })
        .then(function (data) {
          if (addToCartBtn) addToCartBtn.disabled = false;

          /* Shopify's /cart/add.js supports the same "sections"
             param as /cart/change.js and returns each section's
             freshly-rendered HTML in data.sections — so the cart
             drawer can be updated straight from this one request,
             no second round-trip needed. */
          if (
            window.UnnamedCart &&
            payload.sections &&
            data &&
            data.sections &&
            data.sections[payload.sections]
          ) {
            window.UnnamedCart.refresh(data.sections[payload.sections]);
          }

          if (window.UnnamedCart) {
            window.UnnamedCart.open();
          } else {
            window.location.href = "/cart";
          }
        })
        .catch(function () {
          if (addToCartBtn) addToCartBtn.disabled = false;
          /* Never fake cart state — fall back to the real native
             submit rather than leaving the customer stuck. */
          form.submit();
        });
    });

    syncFromSelection();
  }

  /* Lightweight wishlist heart — local-only preference, no account
     sync. Persisted in localStorage keyed by product handle so the
     state survives a page reload / repeat visit on the same device. */
  function initWishlist(wrapper) {
    if (wrapper.dataset.wishlistInit === "true") return;
    wrapper.dataset.wishlistInit = "true";

    var btn = wrapper.querySelector("[data-wishlist-toggle]");
    var handle = wrapper.getAttribute("data-product-handle");
    if (!btn || !handle) return;

    var storageKey = "unnamed:wishlist";

    function readWishlist() {
      try {
        var raw = window.localStorage.getItem(storageKey);
        var parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }

    function writeWishlist(list) {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(list));
      } catch (e) {
        /* Private browsing / storage disabled — fail silently, the
           heart still toggles visually for this page view. */
      }
    }

    function setActive(active) {
      btn.setAttribute("data-active", active ? "true" : "false");
      btn.setAttribute("aria-pressed", active ? "true" : "false");
    }

    setActive(readWishlist().indexOf(handle) !== -1);

    btn.addEventListener("click", function () {
      var list = readWishlist();
      var index = list.indexOf(handle);
      var active;

      if (index === -1) {
        list.push(handle);
        active = true;
      } else {
        list.splice(index, 1);
        active = false;
      }

      writeWishlist(list);
      setActive(active);
    });
  }
})();
